/**
 * Phase 3b §3.1 Section E — Chronic care reveal (Version B, employee-facing).
 *
 * Copy source: `/app/frontend/src/copy/narrative.js` → `CHRONIC_CARE_V2_TEXT`.
 * Renders VERBATIM. Paragraph ordering is preserved exactly as Nigel
 * supplied; the fourth paragraph swaps to the rider-case variant only if
 * `NARRATIVE_CHRONIC_CARE_IS_RIDER` is flipped (currently false).
 *
 * Editorial rules from the brief:
 *   - "One more thing, before you finish." → Fraunces 900, coral accent.
 *   - "It's part of your policy." → bold emphasis.
 *   - The word "dignity" is load-bearing (never elided).
 *   - Closing line "Your employer didn't have to give you this. They did anyway."
 *     is load-bearing; visually emphasised, italic Fraunces, its own line.
 *   - Specific grounding ("unable to bathe, dress, or move on your own",
 *     "an aide who comes to your apartment…") stays literal.
 */
import React from "react";

import { CHRONIC_CARE_V2_TEXT } from "../../copy/narrative";

/**
 * Render a paragraph, splitting on `*token*` markers so the single
 * inline italic ("available to *you*") renders as <em>you</em>. Each
 * paragraph keeps its text byte-for-byte from the source module.
 */
function renderInlineEm(text, keyPrefix) {
    const parts = text.split(/\*([^*]+)\*/g);
    return parts.map((chunk, idx) => {
        // Odd indices are captured (italic) segments.
        if (idx % 2 === 1) {
            return <em key={`${keyPrefix}-${idx}`}>{chunk}</em>;
        }
        return <React.Fragment key={`${keyPrefix}-${idx}`}>{chunk}</React.Fragment>;
    });
}

/**
 * The emphasised paragraph has its FIRST sentence rendered bold for the
 * visual "It's part of your policy." (or rider-case "You can add this to
 * your coverage.") call-out. The remainder stays regular weight.
 */
function renderEmphasisedParagraph(text) {
    // Split on the first sentence-ending period + space.
    const match = text.match(/^([^.]+\.)\s*(.*)$/);
    if (!match) {
        return <strong>{text}</strong>;
    }
    const [, lead, rest] = match;
    return (
        <>
            <strong className="pe-chronic-strong">{lead}</strong>{" "}
            {rest}
        </>
    );
}

export default function ChronicCareV2() {
    const t = CHRONIC_CARE_V2_TEXT;
    return (
        <section className="pe-chronic" data-testid="pe-chronic">
            <h2 className="pe-chronic-h2">{t.hook}</h2>
            <div className="pe-chronic-body">
                {t.paragraphs.map((p, i) => (
                    <p
                        key={i}
                        className={
                            "pe-chronic-p" +
                            (i === t.emphasisedParagraphIndex ? " pe-chronic-emph" : "")
                        }
                        data-testid={`pe-chronic-p-${i}`}
                    >
                        {i === t.emphasisedParagraphIndex
                            ? renderEmphasisedParagraph(p)
                            : renderInlineEm(p, `p${i}`)}
                    </p>
                ))}
                <p
                    className="pe-chronic-closer"
                    data-testid="pe-chronic-closer"
                >
                    {t.closing}
                </p>
            </div>
        </section>
    );
}
